import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Loader2, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatInTz, propertyTimezone } from "@/lib/timezone";
import PublishOSReport from "@/components/PublishOSReport";
import { attention, canStart } from "@/lib/publishosView";

/**
 * Newsletter page → history of what was drafted, scheduled or sent.
 *
 * The rows are deliberately just subject + date, because that's what you
 * scan for. Expanding one fetches its detail and lists the articles and
 * the advertisers it carried — advertisers as text, since the point is
 * to check who ran and when, not to look at the artwork again.
 *
 * `db.newsletters` already stores the full article and ad lists at draft,
 * schedule and send time, so this is a read-side view of records that
 * exist (including for newsletters sent before this panel was built).
 *
 * Status comes from the server already resolved: a scheduled newsletter
 * whose time has passed arrives as "sent".
 *
 * Delete: a trash icon per row opens a three-way confirm — delete here
 * AND in MailerLite, delete here only, or cancel. Rows for newsletters
 * that went out show the icon to admins only (the server enforces it).
 */

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

function StatusPill({ status }) {
  const label =
    status === "sent" ? "Sent" : status === "scheduled" ? "Scheduled" : "Draft";
  const tone =
    status === "sent"
      ? "border-[var(--accent)] text-[var(--accent)]"
      : status === "scheduled"
        ? "border-[var(--ink)] text-[var(--ink)]"
        : "border-[var(--border-strong)] text-[var(--ink-muted)]";
  return (
    <span
      className={`text-[10px] font-mono-display uppercase tracking-wider px-1.5 py-0.5 border ${tone}`}
    >
      {label}
    </span>
  );
}

function HistoryDetail({ id, publishosEnabled }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["newsletter-detail", id],
    queryFn: () => api.newsletterDetail(id),
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 text-xs text-[var(--ink-muted)]">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading…
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-4 py-3 text-xs text-red-700">
        Couldn&apos;t load this newsletter&apos;s contents.
      </div>
    );
  }

  const articles = data?.articles || [];
  const ads = data?.ads || [];

  return (
    <div className="px-4 py-3 border-t border-[var(--border)] grid sm:grid-cols-2 gap-4">
      <div>
        <h4 className="text-[11px] font-mono-display uppercase tracking-wider text-[var(--ink-muted)] mb-1.5">
          Articles ({articles.length})
        </h4>
        {articles.length === 0 ? (
          <p className="text-xs text-[var(--ink-muted)]">None recorded.</p>
        ) : (
          <ol className="space-y-1 list-decimal list-inside">
            {articles.map((a, i) => (
              <li key={a.article_id || i} className="text-xs text-[var(--ink)]">
                {a.wordpress_url ? (
                  <a
                    href={a.wordpress_url}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:underline"
                  >
                    {a.headline || "(untitled)"}
                  </a>
                ) : (
                  a.headline || "(untitled)"
                )}
              </li>
            ))}
          </ol>
        )}
      </div>
      <div>
        <h4 className="text-[11px] font-mono-display uppercase tracking-wider text-[var(--ink-muted)] mb-1.5">
          Advertisers ({ads.length})
        </h4>
        {ads.length === 0 ? (
          <p className="text-xs text-[var(--ink-muted)]">None.</p>
        ) : (
          <ul className="space-y-1">
            {ads.map((ad, i) => (
              <li key={ad.ad_id || i} className="text-xs text-[var(--ink)]">
                {ad.link_url ? (
                  <a
                    href={ad.link_url}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:underline"
                  >
                    {ad.name || "(unnamed)"}
                  </a>
                ) : (
                  ad.name || "(unnamed)"
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {(data?.publishos || canStart(data, publishosEnabled)) && (
        <div className="sm:col-span-2">
          <PublishOSReport
            newsletterId={id}
            report={data?.publishos}
            startable={canStart(data, publishosEnabled)}
          />
        </div>
      )}
    </div>
  );
}

/** What "delete completely" does in MailerLite, per status. */
function everywhereHint(status) {
  if (status === "scheduled") {
    return "Cancels the scheduled send and deletes the campaign in MailerLite.";
  }
  if (status === "sent") {
    return "Deletes the campaign in MailerLite too, including its report there.";
  }
  return "Deletes the draft in MailerLite too.";
}

function DeleteDialog({ row, tz, busy, onChoose, onClose }) {
  const scheduled = row.status === "scheduled";
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={busy ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        data-testid="newsletter-delete-dialog"
        className="bg-white w-full max-w-md border border-[var(--border)] p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="font-serif-display text-lg text-[var(--ink)]">
            Delete this newsletter?
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-[var(--ink-muted)] hover:text-[var(--ink)] disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-[var(--ink)] mb-1 truncate">
          {row.subject || "(no subject)"}
        </p>
        <p className="text-xs text-[var(--ink-muted)] mb-4">
          Its articles will no longer show as used in the picker.
        </p>

        <div className="space-y-2">
          <button
            type="button"
            data-testid="newsletter-delete-everywhere"
            onClick={() => onChoose("everywhere")}
            disabled={busy}
            className="w-full text-left px-3 py-2 border border-[var(--border-strong)] hover:bg-[var(--paper-hover)] disabled:opacity-50"
          >
            <span className="flex items-center gap-2 text-sm text-red-700 font-semibold">
              {busy === "everywhere" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Yes, delete completely
            </span>
            <span className="block text-xs text-[var(--ink-muted)] mt-0.5">
              {everywhereHint(row.status)}
            </span>
          </button>

          <button
            type="button"
            data-testid="newsletter-delete-local"
            onClick={() => onChoose("newsdesk_only")}
            disabled={busy}
            className="w-full text-left px-3 py-2 border border-[var(--border-strong)] hover:bg-[var(--paper-hover)] disabled:opacity-50"
          >
            <span className="flex items-center gap-2 text-sm text-[var(--ink)] font-semibold">
              {busy === "newsdesk_only" && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              Yes, but keep it in MailerLite
            </span>
            {scheduled ? (
              <span className="block text-xs text-amber-700 mt-0.5">
                It will STILL SEND on{" "}
                {formatInTz(row.scheduled_for, tz, "compact")} unless you
                cancel it in MailerLite.
              </span>
            ) : (
              <span className="block text-xs text-[var(--ink-muted)] mt-0.5">
                Only removes it from Newsdesk History.
              </span>
            )}
          </button>

          <button
            type="button"
            data-testid="newsletter-delete-cancel"
            onClick={onClose}
            disabled={busy}
            className="w-full px-3 py-2 text-sm text-[var(--ink-muted)] hover:text-[var(--ink)] disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function NewsletterHistoryPanel() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [openId, setOpenId] = useState(null);
  const [pending, setPending] = useState(null); // row awaiting delete confirm
  const { data: rows = [], isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["newsletters"],
    queryFn: () => api.listNewsletters(),
  });
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.getSettings(),
  });
  const tz = propertyTimezone(settings);
  // PublishOS link on for this property? (Lets old sent newsletters be
  // reported by hand from their detail.)
  const { data: publishos } = useQuery({
    queryKey: ["publishos-status"],
    queryFn: () => api.publishosStatus(),
    staleTime: 5 * 60_000,
  });

  const del = useMutation({
    mutationFn: ({ id, mode }) => api.deleteNewsletter(id, mode),
    onSuccess: (r) => {
      toast.success(
        r?.mailerlite === "deleted"
          ? "Deleted here and in MailerLite."
          : r?.mailerlite === "kept"
            ? "Removed from History. MailerLite was left as is."
            : "Removed from History.",
      );
      setPending(null);
      qc.invalidateQueries({ queryKey: ["newsletters"] });
      // Its articles lose their "used" badge in the picker.
      qc.invalidateQueries({ queryKey: ["newsletter-candidates"] });
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Delete failed");
      // A failed delete can still have changed state (e.g. a schedule
      // cancelled in MailerLite), so refresh the list either way.
      qc.invalidateQueries({ queryKey: ["newsletters"] });
      setPending(null);
    },
  });

  const canDelete = (n) => n.status !== "sent" || isAdmin;

  const rowDate = (n) =>
    n.status === "scheduled"
      ? formatInTz(n.scheduled_for, tz, "compact")
      : fmtDate(n.sent_at || n.created_at);

  return (
    <div className="bg-white border border-[var(--border)] p-5 mb-6">
      <h2 className="font-serif-display text-xl mb-3">
        History{error ? "" : ` (${rows.length})`}
      </h2>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)] py-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      ) : error ? (
        <div
          data-testid="newsletter-history-error"
          className="flex flex-wrap items-center gap-3 text-sm text-red-700 py-2"
        >
          <span>
            Couldn&apos;t load newsletter history
            {error?.response?.data?.detail
              ? `: ${error.response.data.detail}`
              : "."}
          </span>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="text-xs px-2 py-1 border border-[var(--border-strong)] text-[var(--ink)] hover:bg-[var(--paper-hover)] disabled:opacity-50"
          >
            {isFetching ? "Retrying…" : "Retry"}
          </button>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[var(--ink-muted)] py-2">
          Nothing drafted, scheduled or sent yet. Newsletters appear here
          once you create a MailerLite draft, schedule one, or send one.
        </p>
      ) : (
        <ul
          data-testid="newsletter-history"
          className="border border-[var(--border)] divide-y divide-[var(--border)]"
        >
          {rows.map((n) => {
            const open = openId === n.id;
            return (
              <li key={n.id}>
                <div className="flex items-center hover:bg-[var(--paper-hover)]">
                  <button
                    type="button"
                    data-testid={`newsletter-history-${n.id}`}
                    onClick={() => setOpenId(open ? null : n.id)}
                    className="flex-1 min-w-0 flex items-center gap-3 pl-4 pr-2 py-2.5 text-left"
                  >
                    {open ? (
                      <ChevronDown className="w-4 h-4 shrink-0 text-[var(--ink-muted)]" />
                    ) : (
                      <ChevronRight className="w-4 h-4 shrink-0 text-[var(--ink-muted)]" />
                    )}
                    <span className="flex-1 min-w-0 text-sm text-[var(--ink)] truncate">
                      {n.subject || "(no subject)"}
                    </span>
                    {attention(n.publishos_state) && (
                      <span
                        data-testid={`newsletter-history-publishos-${n.id}`}
                        className={`text-[10px] font-mono-display uppercase tracking-wider shrink-0 ${
                          attention(n.publishos_state) === "failed"
                            ? "text-red-700"
                            : "text-amber-700"
                        }`}
                      >
                        {attention(n.publishos_state) === "failed"
                          ? "PublishOS: retry"
                          : "PublishOS: decide"}
                      </span>
                    )}
                    <StatusPill status={n.status} />
                    <span className="text-xs text-[var(--ink-muted)] shrink-0 whitespace-nowrap text-right">
                      {rowDate(n)}
                    </span>
                  </button>
                  <div className="w-9 shrink-0 flex justify-center pr-2">
                    {canDelete(n) && (
                      <button
                        type="button"
                        data-testid={`newsletter-history-delete-${n.id}`}
                        title="Delete"
                        onClick={() => setPending(n)}
                        className="p-1 text-[var(--ink-muted)] hover:text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                {open && (
                  <HistoryDetail id={n.id} publishosEnabled={!!publishos?.enabled} />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {pending && (
        <DeleteDialog
          row={pending}
          tz={tz}
          busy={del.isPending ? del.variables?.mode : null}
          onChoose={(mode) => del.mutate({ id: pending.id, mode })}
          onClose={() => {
            if (!del.isPending) setPending(null);
          }}
        />
      )}
    </div>
  );
}
